<template>
  <v-card class="session-generation">
    <v-card-text>
      <v-form ref="form1" lazy-validation>
        <v-text-field
          v-model="data.description"
          prepend-icon="short_text"
          :rules="$rules('app.required', 'app.text.min', 'app.text.small')"
          :label="$t('scheduler.session.fields.description') + ' *'"
        ></v-text-field>

        <h1 class="title">
          {{ $t("scheduler.session.creation.step-1.events") }}
        </h1>
        <v-text-field
          v-model="data.events.image"
          prepend-icon="image"
          :rules="$rules('app.image')"
          :label="$t('scheduler.event.fields.image')"
        ></v-text-field>
        <v-text-field
          v-model="data.events.abstract"
          prepend-icon="short_text"
          :rules="$rules('app.text.small')"
          :label="$t('scheduler.event.fields.abstract')"
        ></v-text-field>
        <v-textarea
          v-model="data.events.description"
          prepend-icon="notes"
          :rules="$rules('app.text.default')"
          :label="$t('scheduler.event.fields.description')"
        ></v-textarea>

        <h1 class="title">
          {{ $t("scheduler.session.creation.step-1.recurrences") }}
        </h1>
        <v-layout>
          <v-flex md6>
            <v-datetime-picker
              v-model="data.start"
              :text-field-props="dateTextProps"
              :label="$t('scheduler.session.fields.start') + ' *'"
            ></v-datetime-picker>
          </v-flex>
          <v-flex md6>
            <v-dialog
              ref="dialog"
              v-model="durationModal"
              :return-value.sync="data.duration"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  slot="activator"
                  v-model="data.duration"
                  prepend-icon="timelapse"
                  :rules="$rules('app.required')"
                  :label="$t('scheduler.event.fields.duration') + ' *'"
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="durationModal"
                v-model="data.duration"
                format="24hr"
                full-width
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="durationModal = false">{{
                  $t("app.buttons.cancel")
                }}</v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.dialog.save(data.duration)"
                  >{{ $t("app.buttons.apply") }}</v-btn
                >
              </v-time-picker>
            </v-dialog>
          </v-flex>
        </v-layout>
        <v-layout>
          <v-flex md6>
            <v-select
              v-model="data.type"
              prepend-icon="list"
              :items="translatedTypes()"
              :rules="$rules('app.required')"
              :label="$t('scheduler.session.fields.type') + ' *'"
            ></v-select>
          </v-flex>
          <v-flex md6>
            <v-text-field
              v-model="data.size"
              type="number"
              prepend-icon="text_fields"
              :rules="
                $rules(
                  'app.required',
                  'app.numbers.strictPositive',
                  'scheduler.session.maxEvents'
                )
              "
              :label="$t('scheduler.session.fields.size') + ' *'"
            />
          </v-flex>
        </v-layout>

        <h1 class="title">
          {{ $t("scheduler.event.participations.title") + " *" }}
        </h1>
        <InputList :value="parsedUsers">
          <template v-slot:edit>
            <UsersSelectionButton
              class="justify-end"
              icon="edit"
              color="primary lighten-1"
              @confirmed="updateParticipants"
            ></UsersSelectionButton>
          </template>
        </InputList>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn outlined @click="$emit('previous')">
        {{ $t("app.buttons.previous") }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn :loading="generating" dark color="primary" @click="generate">
        {{ $t("app.buttons.next") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import UsersSelectionButton from "@/modules/user/components/UsersSelectionButton";
import InputList from "@/components/utilities/forms/InputList";

export default {
  components: {
    UsersSelectionButton,
    InputList
  },

  data: () => ({
    generating: false,
    durationModal: false,
    data: {
      description: null,
      start: null,
      duration: null,
      type: null,
      size: null,
      events: {
        image: null,
        abstract: null,
        description: null
      },
      participants: []
    },
    rec_types: ["daily", "weekly", "monthly"]
  }),

  computed: {
    parsedUsers: function() {
      var result = [];
      this.data.participants.forEach(user => {
        result.push(this.displayUser(user));
      });
      return result;
    },

    dateTextProps: function() {
      return {
        rules: this.$rules("app.required"),
        prependIcon: "schedule"
      };
    }
  },

  methods: {
    translatedTypes() {
      var self = this;
      var types = [];
      this.rec_types.forEach(type => {
        types.push(self.$t("scheduler.session.recurrence." + type));
      });
      return types;
    },

    displayUser(user) {
      return (
        user.firstname +
        " " +
        user.lastname +
        " (" +
        this.roleToText(user.role) +
        ")"
      );
    },

    roleToText(role) {
      if (!role) {
        return this.$t("user.roles.undefined");
      }
      return this.$t("user.roles." + role + ".title");
    },

    updateParticipants(participants) {
      this.$set(this.data, "participants", participants);
    },

    generate() {
      if (this.$refs.form1.validate()) {
        var self = this;
        this.generating = true;
        var events = [];
        const duration = {
          hours: this.data.duration.split(":")[0],
          minutes: this.data.duration.split(":")[1]
        };

        for (var i = 0; i < this.data.size; i++) {
          const start = this.getStart(i).format(
            process.env.VUE_APP_DATETIME_FORMAT
          );
          var end = this.$moment(start).add(duration.hours, "hours");
          end = end
            .add(duration.minutes, "minutes")
            .format(process.env.VUE_APP_DATETIME_FORMAT);

          var event = {
            id: i,
            all_day: 0,
            start: start,
            end: end,
            title: "Cours " + (i + 1),
            url: null,
            rendering: null,
            abstract: self.data.events.abstract,
            description: self.data.events.description,
            image: self.data.events.image,
            participations: JSON.parse(JSON.stringify(self.data.participants))
          };
          events.push(event);
        }

        const session = {
          description: self.data.description,
          events: events
        };
        this.$emit("next", session);
        this.generating = false;
      }
    },

    getStart(occurence) {
      if (this.data.type === this.$t("scheduler.session.recurrence.daily")) {
        return this.$moment(this.data.start).add(occurence, "days");
      }
      if (this.data.type === this.$t("scheduler.session.recurrence.weekly")) {
        return this.$moment(this.data.start).add(occurence, "weeks");
      }
      if (this.data.type === this.$t("scheduler.session.recurrence.monthly")) {
        return this.$moment(this.data.start).add(occurence, "months");
      }
    }
  }
};
</script>
